import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from './components/layout/AppLayout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { KeyboardShortcutProvider } from './context/KeyboardShortcutContext';
import { ThemeProvider } from './context/ThemeContext';

// Public Pages
import Homepage from './pages/Homepage';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';

// Dashboard Pages (Protected)
import CampaignDashboard from './pages/CampaignDashboard';
import PlatformComparison from './pages/PlatformComparison';
import SpendDashboard from './pages/SpendDashboard';
import DemographicHeatmap from './pages/DemographicHeatmap';
import { SegmentSettings } from './pages/SegmentSettings';
import { IntegrationsPage } from './pages/IntegrationsPage';
import BoardsList from './pages/BoardsList';
import BoardView from './pages/BoardView';
import { AutoPauseRulesPage } from './pages/AutoPauseRulesPage';
import { ABTestManagerPage } from './pages/ABTestManagerPage';
import { CreativeLibraryPage } from './pages/CreativeLibraryPage';
import { BudgetPacingPage } from './pages/BudgetPacingPage';
import { CustomMetricsPage } from './pages/CustomMetricsPage';
import OpportunitiesPage from './pages/OpportunitiesPage';
import AnalyticsOverview from './pages/AnalyticsOverview';
import ProfilePage from './pages/ProfilePage';
import CampaignDetailPage from './pages/CampaignDetailPage';
import AudienceManagerPage from './pages/AudienceManagerPage';
import AdAccountManagerPage from './pages/AdAccountManagerPage';

// New Missing Pages (Phase 1)
import { AlertHistoryPage } from './pages/AlertHistoryPage';
import { APIKeysPage } from './pages/APIKeysPage';
import { WebhooksPage } from './pages/WebhooksPage';
import { ActionLogPage } from './pages/ActionLogPage';
import { CreativeDetailPage } from './pages/CreativeDetailPage';
import { ConversionFunnelPage } from './pages/ConversionFunnelPage';
import { ReportsBuilderPage } from './pages/ReportsBuilderPage';

// Team Management (E007-S026)
import { TeamManagementPage } from './pages/TeamManagementPage';

// Approval Workflow (PRD-3602)
import ApprovalsPage from './pages/ApprovalsPage';

// AI Copy Studio (E007-S024)
import AICopyStudioPage from './pages/AICopyStudioPage';
import { CampaignFormWithAI } from './components/CampaignFormWithAI';
import { AICopyPerformancePage } from './pages/AICopyPerformancePage';

// ML Improvement Dashboard (PRD-3619)
import MLImprovementDashboard from './pages/MLImprovementDashboard';

// Budget Adjustment Demo (PRD-3604)
import BudgetAdjustmentDemo from './pages/BudgetAdjustmentDemo';

// Trend Indicators (E007-S014)
import TrendIndicatorsPage from './pages/TrendIndicatorsPage';

// Emotional Tone Analytics (E007-S018)
import ToneAnalyticsPage from './pages/ToneAnalyticsPage';

// AI Chat Assistant (E007-S028)
import AIChatPage from './pages/AIChatPage';

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }
  
  return <AppLayout>{children}</AppLayout>;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <KeyboardShortcutProvider>
        <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Homepage />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        
        {/* Protected Dashboard Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><CampaignDashboard /></ProtectedRoute>} />
        <Route path="/campaigns" element={<ProtectedRoute><CampaignDashboard /></ProtectedRoute>} />
        <Route path="/campaigns/create" element={<ProtectedRoute><CampaignFormWithAI /></ProtectedRoute>} />
        <Route path="/campaigns/:campaignId" element={<ProtectedRoute><CampaignDetailPage /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><AnalyticsOverview /></ProtectedRoute>} />
        <Route path="/platform-comparison" element={<ProtectedRoute><PlatformComparison /></ProtectedRoute>} />
        <Route path="/spend" element={<ProtectedRoute><SpendDashboard /></ProtectedRoute>} />
        <Route path="/demographics" element={<ProtectedRoute><DemographicHeatmap /></ProtectedRoute>} />
        <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
        <Route path="/settings/segment" element={<ProtectedRoute><SegmentSettings /></ProtectedRoute>} />
        <Route path="/settings/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        
        {/* Team Management (E007-S026) - Under Settings */}
        <Route path="/settings/team" element={<ProtectedRoute><TeamManagementPage /></ProtectedRoute>} />
        
        {/* Ad Account Manager (E007-S029) - Under Settings */}
        <Route path="/settings/ad-accounts" element={<ProtectedRoute><AdAccountManagerPage /></ProtectedRoute>} />
        <Route path="/ad-accounts" element={<ProtectedRoute><AdAccountManagerPage /></ProtectedRoute>} />

        {/* Creative Boards Routes */}
        <Route path="/boards" element={<ProtectedRoute><BoardsList /></ProtectedRoute>} />
        <Route path="/boards/:boardId" element={<ProtectedRoute><BoardView /></ProtectedRoute>} />
        
        {/* Epic 7 Feature Routes */}
        <Route path="/opportunities" element={<ProtectedRoute><OpportunitiesPage /></ProtectedRoute>} />
        <Route path="/auto-pause-rules" element={<ProtectedRoute><AutoPauseRulesPage /></ProtectedRoute>} />
        <Route path="/ab-tests" element={<ProtectedRoute><ABTestManagerPage /></ProtectedRoute>} />
        <Route path="/creative-library" element={<ProtectedRoute><CreativeLibraryPage /></ProtectedRoute>} />
        <Route path="/budget-pacing" element={<ProtectedRoute><BudgetPacingPage /></ProtectedRoute>} />
        <Route path="/custom-metrics" element={<ProtectedRoute><CustomMetricsPage /></ProtectedRoute>} />
        <Route path="/audiences" element={<ProtectedRoute><AudienceManagerPage /></ProtectedRoute>} />
        
        {/* AI Copy Studio (E007-S024) */}
        <Route path="/ai-copy" element={<ProtectedRoute><AICopyStudioPage /></ProtectedRoute>} />
        <Route path="/ai-copy-studio" element={<ProtectedRoute><AICopyStudioPage /></ProtectedRoute>} />
        <Route path="/ai-copy/performance" element={<ProtectedRoute><AICopyPerformancePage /></ProtectedRoute>} />
        
        {/* ML Improvement Dashboard (PRD-3619) */}
        <Route path="/ml-dashboard" element={<ProtectedRoute><MLImprovementDashboard /></ProtectedRoute>} />
        <Route path="/ml-learning" element={<ProtectedRoute><MLImprovementDashboard /></ProtectedRoute>} />
        
        {/* New Missing Pages Routes (Phase 1) */}
        <Route path="/alerts" element={<ProtectedRoute><AlertHistoryPage /></ProtectedRoute>} />
        <Route path="/alert-history" element={<ProtectedRoute><AlertHistoryPage /></ProtectedRoute>} />
        <Route path="/settings/api-keys" element={<ProtectedRoute><APIKeysPage /></ProtectedRoute>} />
        <Route path="/settings/webhooks" element={<ProtectedRoute><WebhooksPage /></ProtectedRoute>} />
        <Route path="/activity" element={<ProtectedRoute><ActionLogPage /></ProtectedRoute>} />
        <Route path="/action-log" element={<ProtectedRoute><ActionLogPage /></ProtectedRoute>} />
        <Route path="/creatives/:creativeId" element={<ProtectedRoute><CreativeDetailPage /></ProtectedRoute>} />
        <Route path="/analytics/funnel" element={<ProtectedRoute><ConversionFunnelPage /></ProtectedRoute>} />
        <Route path="/reports/builder" element={<ProtectedRoute><ReportsBuilderPage /></ProtectedRoute>} />
        
        {/* Approval Workflow (PRD-3602) */}
        <Route path="/approvals" element={<ProtectedRoute><ApprovalsPage /></ProtectedRoute>} />
        
        {/* Budget Adjustment (PRD-3604) */}
        <Route path="/budget-adjustment" element={<ProtectedRoute><BudgetAdjustmentDemo /></ProtectedRoute>} />
        
        {/* Trend Indicators (E007-S014) */}
        <Route path="/trends" element={<ProtectedRoute><TrendIndicatorsPage /></ProtectedRoute>} />
        
        {/* Emotional Tone Analytics (E007-S018) */}
        <Route path="/tone-analytics" element={<ProtectedRoute><ToneAnalyticsPage /></ProtectedRoute>} />
        <Route path="/emotional-tone" element={<ProtectedRoute><ToneAnalyticsPage /></ProtectedRoute>} />
        
        {/* AI Chat Assistant (E007-S028) */}
        <Route path="/ai-chat" element={<ProtectedRoute><AIChatPage /></ProtectedRoute>} />
        <Route path="/assistant" element={<ProtectedRoute><AIChatPage /></ProtectedRoute>} />
        
          {/* Catch all - redirect to homepage */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </KeyboardShortcutProvider>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
